/**
 * Admin-specific API calls for the ZeroSmoke dashboard.
 */
import { getToken } from './client';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

async function adminFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string> ?? {}),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(body.detail ?? 'Request failed');
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

// ── Types ────────────────────────────────────────────────────────
export interface DashboardStats {
  total_revenue: number;
  total_orders: number;
  total_customers: number;
  total_products: number;
  pending_orders: number;
  low_stock_count: number;
  active_coupons: number;
  avg_order_value: number;
}

export interface SalesSummary {
  period: string;
  total_orders: number;
  total_revenue: number;
  avg_order_value: number;
}

export interface SalesDataPoint {
  label: string;
  revenue: number;
  orders: number;
}

export interface TopProduct {
  product_id: string;
  product_name: string;
  brand: string;
  total_sold: number;
  total_revenue: number;
}

export interface BrandSalesData {
  brand: string;
  revenue: number;
  orders: number;
}

export interface CustomerMetric {
  total_customers: number;
  new_customers: number;
  returning_customers: number;
  return_rate: number;
}

export interface OrderMetric {
  total_orders: number;
  new_orders: number;
  returning_orders: number;
  repeat_rate: number;
}

export interface CouponOut {
  id: string;
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_order_value: number;
  max_discount: number | null;
  usage_limit: number | null;
  used_count: number;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  created_at: string;
}

export interface CouponCreate {
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  min_order_value?: number;
  max_discount?: number | null;
  usage_limit?: number | null;
  valid_from?: string | null;
  valid_until?: string | null;
  is_active?: boolean;
}

export interface OrderAdmin {
  id: string;
  status: string;
  total: number;
  shipping: number;
  coupon_code: string | null;
  discount_amount: number | null;
  coins_redeemed: number | null;
  coin_discount: number | null;
  ship_first_name: string;
  ship_last_name: string;
  ship_email: string;
  ship_phone: string;
  ship_address: string;
  ship_city: string;
  ship_state: string;
  ship_pincode: string;
  tracking_id: string | null;
  tracking_url: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  referrer: string | null;
  landing_page: string | null;
  device_type: string | null;
  session_page_views: number | null;
  created_at: string;
  exported_to_sheets_at: string | null;
  items: { id: string; product_id: string | null; product_name: string; unit_price: number; quantity: number }[];
}

export interface LLMAnalysis {
  analysis: string;
  generated_at: string;
}

export interface UserAdmin {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  is_admin: boolean;
  is_active: boolean;
  created_at: string;
  orders_count: number;
  total_spent: number;
}

// ── Rewards (admin) ──────────────────────────────────────────────
export interface AdminCoinTx {
  id: string;
  type: string;
  amount: number;
  status: string;
  note: string | null;
  balance_after: number;
  created_at: string;
}
export interface AdminReferral {
  id: string;
  status: 'pending' | 'qualified' | 'reversed';
  referee_bonus: number;
  referrer_bonus: number;
  signup_ip: string | null;
  signup_phone: string | null;
  created_at: string;
  qualified_at: string | null;
  referrer_name: string | null;
  referrer_email: string | null;
  referee_name: string | null;
  referee_email: string | null;
}
export interface AdminUserCoins {
  user: { email: string; first_name: string };
  balance: number;
  history: AdminCoinTx[];
  has_more: boolean;
}
export const adminRewards = {
  referrals: (cursor?: string | null) =>
    adminFetch<{ items: AdminReferral[]; has_more: boolean }>(
      `/rewards/admin/referrals${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),
  userCoins: (userId: string, cursor?: string | null) =>
    adminFetch<AdminUserCoins>(
      `/rewards/admin/user/${userId}/coins${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
    ),
  adjust: (userId: string, amount: number, note?: string) =>
    adminFetch<{ balance: number }>(`/rewards/admin/user/${userId}/adjust`, {
      method: 'POST',
      body: JSON.stringify({ amount, note }),
    }),
};

// ── Analytics API ────────────────────────────────────────────────
export const adminAnalytics = {
  dashboard: () => adminFetch<DashboardStats>('/admin/analytics/dashboard'),

  salesSummary: (period: string = 'month') =>
    adminFetch<SalesSummary>(`/admin/analytics/sales-summary?period=${period}`),

  salesChart: (granularity: string = 'daily') =>
    adminFetch<SalesDataPoint[]>(`/admin/analytics/sales-chart?granularity=${granularity}`),

  topProducts: (period: string = 'month', limit: number = 10) =>
    adminFetch<TopProduct[]>(`/admin/analytics/top-products?period=${period}&limit=${limit}`),

  brandSales: (period: string = 'month') =>
    adminFetch<BrandSalesData[]>(`/admin/analytics/brand-sales?period=${period}`),

  customerMetrics: (period: 'today' | 'week' | 'month' | 'year' | 'all' = 'month') =>
    adminFetch<CustomerMetric>(`/admin/analytics/customer-metrics?period=${period}`),

  orderMetrics: (period: 'today' | 'week' | 'month' | 'year' | 'all' = 'month') =>
    adminFetch<OrderMetric>(`/admin/analytics/order-metrics?period=${period}`),

  recentOrders: (limit: number = 20) =>
    adminFetch<OrderAdmin[]>(`/admin/analytics/recent-orders?limit=${limit}`),
};

// ── Coupons API ──────────────────────────────────────────────────
export const adminCoupons = {
  list: () => adminFetch<CouponOut[]>('/coupons'),

  create: (data: CouponCreate) =>
    adminFetch<CouponOut>('/coupons', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: Partial<CouponCreate>) =>
    adminFetch<CouponOut>(`/coupons/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  delete: (id: string) =>
    adminFetch<{ detail: string }>(`/coupons/${id}`, { method: 'DELETE' }),
};

// ── Wholesale Inquiries ──────────────────────────────────────────
export type WholesaleStatus = 'new' | 'contacted' | 'closed';

export interface WholesaleInquiryOut {
  id: string;
  name: string;
  business_name: string;
  phone: string;
  email: string;
  city: string;
  products: string;
  quantity: number;
  message: string;
  status: WholesaleStatus;
  created_at: string;
}

export interface WholesaleInquiryUpdate {
  name?: string;
  business_name?: string;
  phone?: string;
  email?: string;
  city?: string;
  products?: string;
  quantity?: number;
  message?: string;
  status?: WholesaleStatus;
}

export const adminWholesale = {
  list: () => adminFetch<WholesaleInquiryOut[]>('/wholesale'),

  update: (id: string, data: WholesaleInquiryUpdate) =>
    adminFetch<WholesaleInquiryOut>(`/wholesale/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  delete: (id: string) =>
    adminFetch<{ detail: string }>(`/wholesale/${id}`, { method: 'DELETE' }),
};

// ── Campaigns ────────────────────────────────────────────────────
export interface CampaignOut {
  id: string;
  slug: string;
  name: string;
  offer_text: string;
  cta_label: string;
  cta_link: string;
  starts_at: string | null;
  ends_at: string | null;
  is_active: boolean;
  created_at: string;
}

export interface CampaignUpdate {
  offer_text?: string;
  cta_label?: string;
  cta_link?: string;
  starts_at?: string | null;
  ends_at?: string | null;
  is_active?: boolean;
}

export const adminCampaigns = {
  list: () => adminFetch<CampaignOut[]>('/campaigns'),

  update: (id: string, data: CampaignUpdate) =>
    adminFetch<CampaignOut>(`/campaigns/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
};

// ── Orders Admin API ─────────────────────────────────────────────
export const adminOrders = {
  list: () => adminFetch<OrderAdmin[]>('/orders/admin/all'),

  updateStatus: (id: string, status: string) =>
    adminFetch<OrderAdmin>(`/orders/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  update: (id: string, data: Record<string, unknown>) =>
    adminFetch<OrderAdmin>(`/orders/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  exportToSheets: async (orderId: string, items?: { product_id: string | null; product_name: string; unit_price: number; quantity: number }[]) => {
    const res = await fetch(`${API_BASE}/admin/sheets/orders/${orderId}/export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}) },
      body: JSON.stringify(items && items.length > 0 ? { items } : {}),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(body.detail ?? 'Export to Sheets failed');
    }
    return res.json() as Promise<{ success: boolean; row: number; sno: number }>;
  },

  downloadReceipt: async (id: string) => {
    const token = getToken();
    const res = await fetch(`${API_BASE}/admin/receipts/${id}/pdf`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error('Failed to download receipt');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ZeroSmoke_Receipt_${id.slice(0, 8)}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  },
};

// ── Products Admin API ───────────────────────────────────────────
export const adminProducts = {
  list: (page = 1, perPage = 200) =>
    adminFetch<{ items: Record<string, unknown>[]; total: number; page: number; per_page: number; pages: number }>(
      `/products?page=${page}&per_page=${perPage}`
    ),

  update: (id: string, data: Record<string, unknown>) =>
    adminFetch<Record<string, unknown>>(`/products/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  create: (data: Record<string, unknown>) =>
    adminFetch<Record<string, unknown>>('/products', { method: 'POST', body: JSON.stringify(data) }),

  delete: (id: string) =>
    adminFetch<{ detail: string }>(`/products/${id}`, { method: 'DELETE' }),

  uploadImage: async (file: File, brand?: string, name?: string, strength?: string): Promise<string> => {
    const token = getToken();
    const form = new FormData();
    form.append('file', file);
    const qp = new URLSearchParams();
    if (brand) qp.set('brand', brand);
    if (name) qp.set('name', name);
    if (strength) qp.set('strength', strength);
    const params = qp.toString() ? `?${qp.toString()}` : '';
    const res = await fetch(`${API_BASE}/products/upload-image${params}`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({ detail: res.statusText }));
      throw new Error(body.detail ?? 'Upload failed');
    }
    const data = await res.json();
    return data.path as string;
  },
};

// ── LLM API ──────────────────────────────────────────────────────
export const adminLLM = {
  analyze: (prompt?: string) =>
    adminFetch<LLMAnalysis>('/admin/llm/analyze', {
      method: 'POST',
      body: JSON.stringify({ prompt: prompt || '' }),
    }),
};

// ── Users Admin API ──────────────────────────────────────────
export const adminUsers = {
  list: () => adminFetch<UserAdmin[]>('/admin/users'),

  get: (id: string) =>
    adminFetch<{ id: string; email: string; first_name: string; last_name: string; phone: string | null; is_active: boolean; created_at: string; addresses: { id: string; address_line: string; city: string; state: string; pincode: string; is_default: boolean }[]; orders: { id: string; status: string; total: number; items_count: number; created_at: string }[] }>(`/admin/users/${id}`),

  update: (id: string, data: { first_name?: string; last_name?: string; email?: string; phone?: string | null; is_active?: boolean }) =>
    adminFetch<UserAdmin>(`/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  delete: (id: string) =>
    adminFetch<void>(`/admin/users/${id}`, { method: 'DELETE' }),

  updateAddress: (userId: string, addressId: string, data: { address_line: string; city: string; state: string; pincode: string; is_default: boolean }) =>
    adminFetch<{ id: string; address_line: string; city: string; state: string; pincode: string; is_default: boolean }>(`/admin/users/${userId}/addresses/${addressId}`, { method: 'PUT', body: JSON.stringify(data) }),

  deleteAddress: (userId: string, addressId: string) =>
    adminFetch<void>(`/admin/users/${userId}/addresses/${addressId}`, { method: 'DELETE' }),
};

// ── Stock Notifications Admin API ────────────────────────────────
export interface StockNotificationAdmin {
  id: string;
  product_id: string;
  email: string;
  notified: boolean;
  created_at: string;
  product_name: string | null;
  user_name: string | null;
  user_phone: string | null;
}

export const adminStockNotifications = {
  list: (productId?: string) =>
    adminFetch<StockNotificationAdmin[]>(
      `/products/admin/stock-notifications${productId ? `?product_id=${productId}` : ''}`
    ),
};

// ── Google Analytics API ─────────────────────────────────────────
export interface GAMetric {
  value: number;
  prev: number;
}

export interface GAOverview {
  configured: boolean;
  period?: string;
  metrics?: {
    active_users: GAMetric;
    sessions: GAMetric;
    page_views: GAMetric;
    avg_session_duration: GAMetric;
    bounce_rate: GAMetric;
    new_users: GAMetric;
    engaged_sessions: GAMetric;
  };
}

export interface GAPage { path: string; views: number; users: number; avg_duration: number }
export interface GASource { source: string; medium: string; sessions: number; users: number; bounce_rate: number }
export interface GADevice { device: string; sessions: number; users: number }
export interface GAGeoItem { name: string; users: number; sessions: number }
export interface GADay { date: string; sessions: number; users: number; page_views: number }

export interface GAStatus {
  has_client_id: boolean;
  has_client_secret: boolean;
  has_refresh_token: boolean;
  has_property_id: boolean;
  redirect_uri: string;
}

function qs(range?: { from: string; to: string }): string {
  if (!range) return '';
  return `?from=${range.from}&to=${range.to}`;
}

export const adminGA = {
  overview: (range?: { from: string; to: string }) =>
    adminFetch<GAOverview>(`/admin/ga/overview${qs(range)}`),
  topPages: (range?: { from: string; to: string }) =>
    adminFetch<{ configured: boolean; pages: GAPage[] }>(`/admin/ga/top-pages${qs(range)}`),
  trafficSources: (range?: { from: string; to: string }) =>
    adminFetch<{ configured: boolean; sources: GASource[] }>(`/admin/ga/traffic-sources${qs(range)}`),
  devices: (range?: { from: string; to: string }) =>
    adminFetch<{ configured: boolean; devices: GADevice[] }>(`/admin/ga/devices${qs(range)}`),
  geo: (range?: { from: string; to: string }) =>
    adminFetch<{ configured: boolean; countries: GAGeoItem[]; cities: GAGeoItem[] }>(`/admin/ga/geo${qs(range)}`),
  dailyTrend: (range?: { from: string; to: string }) =>
    adminFetch<{ configured: boolean; days: GADay[] }>(`/admin/ga/daily-trend${qs(range)}`),
  status: () => adminFetch<GAStatus>('/admin/ga/status'),
  oauthUrl: () => adminFetch<{ url: string; redirect_uri: string }>('/admin/ga/oauth/url'),
};

// ── Inventory Purchases (PO) API ─────────────────────────────────
// A purchase is a cost/accounting record only — it does NOT affect stock.
export interface InventoryPurchaseItem {
  id: string;
  product_id: string | null;
  product_name: string;
  quantity: number;
}

export interface InventoryPurchase {
  id: string;
  currency: string;
  product_subtotal: number;
  product_subtotal_inr: number;
  shipping_cost: number;
  customs_charges: number;
  amount: number;
  status: 'order_booked' | 'payment_done' | 'in_transit' | 'received' | 'cancelled';
  order_date: string;
  received_date: string | null;
  supplier: string | null;
  comment: string | null;
  created_at: string;
  items: InventoryPurchaseItem[];
}

export interface InventoryPurchaseInput {
  currency?: string;
  product_subtotal?: number;
  product_subtotal_inr?: number;
  shipping_cost?: number;
  customs_charges?: number;
  amount: number;
  status?: string;
  order_date?: string | null;
  received_date?: string | null;
  supplier?: string | null;
  comment?: string | null;
  items: { product_id: string; quantity: number }[];
}

export const adminInventoryPurchases = {
  list: (params?: { status?: string; from?: string; to?: string }) => {
    const qp = new URLSearchParams();
    if (params?.status) qp.set('status', params.status);
    if (params?.from) qp.set('from', params.from);
    if (params?.to) qp.set('to', params.to);
    const s = qp.toString();
    return adminFetch<InventoryPurchase[]>(`/admin/inventory-purchases${s ? `?${s}` : ''}`);
  },
  create: (data: InventoryPurchaseInput) =>
    adminFetch<InventoryPurchase>('/admin/inventory-purchases', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<InventoryPurchaseInput>) =>
    adminFetch<InventoryPurchase>(`/admin/inventory-purchases/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) =>
    adminFetch<{ detail: string }>(`/admin/inventory-purchases/${id}`, { method: 'DELETE' }),
};

// ── Expenses API ─────────────────────────────────────────────────
export interface Expense {
  id: string;
  category: string;
  amount: number;
  expense_date: string;
  vendor: string | null;
  payment_method: string | null;
  payment_status: string;
  comment: string | null;
  created_at: string;
}

export interface ExpenseInput {
  category: string;
  amount: number;
  expense_date?: string | null;
  vendor?: string | null;
  payment_method?: string | null;
  payment_status?: string;
  comment?: string | null;
}

export const adminExpenses = {
  list: (params?: { from?: string; to?: string; category?: string }) => {
    const qp = new URLSearchParams();
    if (params?.from) qp.set('from', params.from);
    if (params?.to) qp.set('to', params.to);
    if (params?.category) qp.set('category', params.category);
    const s = qp.toString();
    return adminFetch<Expense[]>(`/admin/expenses${s ? `?${s}` : ''}`);
  },
  create: (data: ExpenseInput) =>
    adminFetch<Expense>('/admin/expenses', { method: 'POST', body: JSON.stringify(data) }),
  createWithInvestment: (data: ExpenseInput & { shareholder_id: string }) =>
    adminFetch<{ expense: Expense; investment: ShareholderTxn }>('/admin/expenses/with-investment', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  update: (id: string, data: Partial<ExpenseInput>) =>
    adminFetch<Expense>(`/admin/expenses/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) =>
    adminFetch<{ detail: string }>(`/admin/expenses/${id}`, { method: 'DELETE' }),
};

// ── Shareholders API ─────────────────────────────────────────────
export interface Shareholder {
  id: string;
  name: string;
  comment: string | null;
  created_at: string;
}

export interface ShareholderInput {
  name: string;
  comment?: string | null;
}

export interface ShareholderTxn {
  id: string;
  shareholder_id: string;
  kind: 'invested' | 'settle';
  amount: number;
  paid_to: string | null;
  txn_date: string;
  payment_method: string | null;
  comment: string | null;
  created_at: string;
}

export interface ShareholderTxnInput {
  shareholder_id: string;
  kind: 'invested' | 'settle';
  amount: number;
  paid_to: string;
  txn_date?: string | null;
  payment_method?: string | null;
  comment?: string | null;
}

export const adminShareholders = {
  list: () => adminFetch<Shareholder[]>('/admin/shareholders'),
  create: (data: ShareholderInput) =>
    adminFetch<Shareholder>('/admin/shareholders', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<ShareholderInput>) =>
    adminFetch<Shareholder>(`/admin/shareholders/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) =>
    adminFetch<{ detail: string }>(`/admin/shareholders/${id}`, { method: 'DELETE' }),
};

// ── Revenue Periods API ──────────────────────────────────────────
export interface RevenuePeriod {
  id: string;
  period_from: string | null;
  period_to: string | null;
  total_orders: number;
  shipway_orders: number;
  porter_orders: number;
  rto_orders: number;
  never_shipped_orders: number;
  cod_count: number;
  prepaid_count: number;
  porter_parcel_count: number;
  qty_sold: number;
  inhouse_qty: number;
  total_revenue: number;
  cod_revenue: number;
  prepaid_revenue: number;
  porter_revenue: number;
  packaging_charges: number;
  shipway_shipping_rto: number;
  shipway_remittance_charges: number;
  shipway_bank_dispersal: number;
  advertising_spent: number;
  other_charges: number;
  inhouse_cost: number;
  total_charges: number;
  revenue_after_expenses: number;
  comments: string | null;
  created_at: string;
  updated_at: string;
  inventory_sales_count?: number;
}

export type RevenuePeriodInput = {
  period_from: string;
  period_to: string;
  comments?: string | null;
} & Partial<Omit<RevenuePeriod, 'id' | 'period_from' | 'period_to' | 'comments' | 'created_at' | 'updated_at'>>;

export const adminRevenuePeriods = {
  list: () => adminFetch<RevenuePeriod[]>('/admin/revenue-periods'),
  create: (data: RevenuePeriodInput) =>
    adminFetch<RevenuePeriod>('/admin/revenue-periods', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<RevenuePeriodInput>) =>
    adminFetch<RevenuePeriod>(`/admin/revenue-periods/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) =>
    adminFetch<{ detail: string }>(`/admin/revenue-periods/${id}`, { method: 'DELETE' }),
};

// ── Inventory Sales API ──────────────────────────────────────────
export interface InventorySalesRow {
  id: string;
  revenue_period_id: string;
  product_name: string;
  quantity: number;
  sold: number;
  inhouse_use: number;
  sold_at: number;
  adjustment_amount: number;
  total: number;
  remarks: string | null;
  created_at: string;
  updated_at: string;
}

export interface InventorySalesInput {
  revenue_period_id: string;
  product_name: string;
  quantity?: number;
  sold?: number;
  inhouse_use?: number;
  sold_at?: number;
  adjustment_amount?: number;
  total?: number;
  remarks?: string | null;
}

export const adminInventorySales = {
  list: (periodId?: string) =>
    adminFetch<InventorySalesRow[]>(`/admin/inventory-sales${periodId ? `?period_id=${periodId}` : ''}`),
  bulkCreate: (data: { revenue_period_id: string; items: Omit<InventorySalesInput, 'revenue_period_id'>[] }) =>
    adminFetch<InventorySalesRow[]>('/admin/inventory-sales/bulk', { method: 'POST', body: JSON.stringify(data) }),
  create: (data: InventorySalesInput) =>
    adminFetch<InventorySalesRow>('/admin/inventory-sales', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<InventorySalesInput>) =>
    adminFetch<InventorySalesRow>(`/admin/inventory-sales/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) =>
    adminFetch<{ detail: string }>(`/admin/inventory-sales/${id}`, { method: 'DELETE' }),
};

export const adminShareholderTxns = {
  list: (params?: { kind?: string; shareholder_id?: string; from?: string; to?: string }) => {
    const qp = new URLSearchParams();
    if (params?.kind) qp.set('kind', params.kind);
    if (params?.shareholder_id) qp.set('shareholder_id', params.shareholder_id);
    if (params?.from) qp.set('from', params.from);
    if (params?.to) qp.set('to', params.to);
    const s = qp.toString();
    return adminFetch<ShareholderTxn[]>(`/admin/shareholder-transactions${s ? `?${s}` : ''}`);
  },
  create: (data: ShareholderTxnInput) =>
    adminFetch<ShareholderTxn>('/admin/shareholder-transactions', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<ShareholderTxnInput>) =>
    adminFetch<ShareholderTxn>(`/admin/shareholder-transactions/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  delete: (id: string) =>
    adminFetch<{ detail: string }>(`/admin/shareholder-transactions/${id}`, { method: 'DELETE' }),
};
